/**
 * Il ciclo della verifica pre-incheck di un periodo.
 *
 * Il lavoro lo fa il backend, un lotto per richiesta: qui si guida soltanto la
 * successione — una richiesta, il cursore che torna, la richiesta dopo — e si
 * traduce l'avanzamento in qualcosa che la modale possa mostrare.
 *
 * Niente Vue e niente axios: il trasporto arriva da fuori (`requestBatch`),
 * cosi' la successione si puo' far girare per intero senza rete. E' l'unica
 * parte che ha degli stati propri — il passaggio di fase, la barra che non
 * torna indietro, il cursore che non avanza — e tenerla separata e' cio' che la
 * rende verificabile.
 */

export const REFRESH_PHASE = {
    CATALOGUE: 'catalogue',
    RECHECK: 'recheck',
};

export const REFRESH_STATUS = {
    SUCCESS: 'success',
    ERROR: 'error',
    ABORTED: 'aborted',
};

/**
 * La percentuale della fase 1, arrotondata e mai sopra il cento.
 *
 * `total` a zero non e' un errore: e' il periodo senza niente da leggere, e li'
 * la passata e' conclusa in partenza — dire cento e' vero, e dire zero
 * lascerebbe la barra ferma su una fase che non ha piu' niente da fare.
 */
const percentOf = (processed, total) =>
{
    if(total <= 0) return 100;
    return Math.min(100, Math.round((processed * 100) / total));
};

/**
 * Vero quando la risposta non e' un lotto ma un errore di dominio.
 *
 * `ZdhcService` ha un contratto solo per tutti i suoi metodi: in caso di errore
 * torna un oggetto con `error`, non solleva. Un `null` e' il corpo vuoto, che
 * qui e' altrettanto inservibile: senza `next_cursor` non si sa nemmeno se
 * continuare.
 */
const isFailure = (batch) => !batch || typeof batch.error === 'string';

/**
 * Guida la passata fino in fondo.
 *
 * @param  {string}   period                    il periodo `YYYY-MM`
 * @param  {object}   deps
 * @param  {Function} deps.requestBatch         `(period, cursor) => Promise<payload|errorShape>`
 * @param  {Function} [deps.onProgress]         chiamata a ogni risposta con lo stato da mostrare
 * @param  {Function} [deps.isTransportFailure] riconosce un guasto di rete fra le eccezioni
 * @return {Promise<{status: string, unresolvedCount: number, error: ?object}>}
 */
export const runPeriodRefresh = async (period, { requestBatch, onProgress = () => {}, isTransportFailure = () => false }) =>
{
    // Nullo, non stringa vuota: la prima richiesta di una passata dichiara di
    // non avere un cursore, e una stringa vuota e' invece un cursore che il
    // client crede di avere — il backend la rifiuta con 422 apposta.
    let cursor = null;
    let phase = REFRESH_PHASE.CATALOGUE;
    let percent = 0;
    let unresolvedCount = 0;

    // Il cursore avanza a ogni risposta non conclusiva: il backend chiude il
    // lotto solo dopo aver scritto qualcosa. Riceverlo identico due volte
    // significa che quella proprieta' e' caduta, e proseguire vorrebbe dire
    // chiedere per sempre lo stesso lotto: meglio un errore visibile di una
    // modale che gira a vuoto.
    let previousCursor = null;

    try
    {
        for(;;)
        {
            const batch = await requestBatch(period, cursor);

            if(isFailure(batch))
            {
                return { status: REFRESH_STATUS.ERROR, unresolvedCount, error: batch ?? null };
            }

            unresolvedCount = batch.unresolved_count ?? 0;

            // Il passaggio di fase avviene **fra** due risposte, mai dentro la
            // stessa. Le due fasi contano cose diverse — pagine di catalogo la
            // prima, prodotti non risolti la seconda — e continuare a calcolare
            // una percentuale con le unita' nuove la farebbe tornare indietro.
            // Da qui in poi non c'e' piu' una percentuale: c'e' un loader.
            if(batch.phase === REFRESH_PHASE.RECHECK) phase = REFRESH_PHASE.RECHECK;

            if(phase === REFRESH_PHASE.CATALOGUE)
            {
                // Il massimo, non l'ultimo valore: la barra non torna indietro
                // nemmeno se una risposta porta un parziale piu' basso.
                percent = Math.max(percent, percentOf(batch.processed ?? 0, batch.total ?? 0));
            }

            onProgress({
                phase,
                percent: phase === REFRESH_PHASE.CATALOGUE ? percent : null,
                processed: batch.processed ?? 0,
                total: batch.total ?? 0,
                unresolvedCount,
            });

            const next = batch.next_cursor ?? null;

            if(next === null) return { status: REFRESH_STATUS.SUCCESS, unresolvedCount, error: null };

            if(next === previousCursor)
            {
                return { status: REFRESH_STATUS.ERROR, unresolvedCount, error: { error: 'zdhc_cursor_stalled' } };
            }

            previousCursor = next;
            // Si rimanda **com'e' arrivato**: e' opaco, e ricostruirlo qui
            // significherebbe fabbricare un cursore che il backend rifiuta.
            cursor = next;
        }
    }
    catch(e)
    {
        // La modale si chiude mentre la passata gira: ogni richiesta passa dal
        // requestManager, che la annulla. Non e' un guasto, e non va mostrato
        // come tale.
        if(e?.message === 'canceled') return { status: REFRESH_STATUS.ABORTED, unresolvedCount, error: null };

        // Il timeout e la connessione caduta **non hanno una risposta**, quindi
        // non arrivano qui come lotto in errore: arrivano come eccezione, e
        // rilanciarle lascerebbe la modale ferma su "caricamento" con una
        // promise rifiutata che nessuno raccoglie — chi guarda non vedrebbe mai
        // il pulsante di riprova. Sono un esito da mostrare, non un guasto del
        // nostro codice.
        //
        // A riconoscerle e' chi conosce il trasporto (`isTransportError`): qui
        // si sa soltanto che vanno trattate come un lotto fallito. Il default
        // non riconosce niente di proposito — un modulo che si dichiara puro
        // non deve indovinare come axios marca le sue eccezioni, e cio' che non
        // e' riconosciuto continua a risalire.
        if(isTransportFailure(e)) return { status: REFRESH_STATUS.ERROR, unresolvedCount, error: { error: 'zdhc_unreachable' } };

        // Un bug nostro deve farsi vedere, non travestirsi da rete caduta.
        throw e;
    }
};
